import { useMemo } from "react";

// Derives the per-DB packing dashboard from the routing payload.
//
// Crate-scoped metric: for each DB, iterate the crates it's directly
// assigned to (= crates with at least one customer whose cp_db_id
// matches this DB), excluding pure third-party shipment crates that
// the delivery app handles. For each such crate, count this DB's
// customers in the crate and estimate the packed fraction as
// `packed_tps / tp_count` — i.e. the same formula PackingInterface-
// Screen.js uses on the detail screen.
//
// Why crate-scoped (not customer-scoped):
// The executive's mental model is "I've packed every crate I was
// assigned → I'm done." A customer-scoped metric (every order today
// for every customer must be cp_packed_at=today) pulls in connecting
// crates owned by other DBs and TOP_SKU inventory items, which can
// drag a DB's progress down for reasons outside the executive's
// control. Crate-scoped intentionally ignores that and matches what
// the detail screen shows when the executive drills in — so the list
// card and the detail card always agree.
//
// Known approximation: when a crate is partially packed and its
// customers span multiple DBs, the formula assumes uniform
// distribution (this DB's share of packed = mine.length *
// packed_tps/tp_count). It's slightly inaccurate but the snapshot
// doesn't surface per-customer-per-crate pack state to do it exactly.
// Detail screen has the same approximation; both stay in sync.

const usePackingDbs = (data) =>
  useMemo(() => {
    const cpDbs = Array.isArray(data?.cp_dbs) ? data.cp_dbs : [];
    const crates = Array.isArray(data?.data?.CRATE) ? data.data.CRATE : [];

    return cpDbs.map((db) => {
      const dbId = String(db.cp_db_id);
      let customers = 0;
      let packed = 0;

      crates.forEach((c) => {
        if (c?.is_shipment) return;
        // Scope to "today" crates only — PackingInterfaceScreen.dbCrates
        // defaults its tab to "today" and filters `is_rescheduled === false`,
        // so the list card must match or we land back in the 18/49 vs 0/32
        // disagreement the user reported on 2026-05-13.
        if (c?.is_rescheduled) return;
        const tps = Array.isArray(c?.tp_list) ? c.tp_list : [];
        const mine = tps.filter(
          (tp) => String(tp?.cp_db_id) === dbId && tp?.status === "ASSIGNED",
        );
        if (mine.length === 0) return;
        customers += mine.length;
        const tpCount = Number(c?.tp_count) || 0;
        const ratio = tpCount > 0 ? Number(c?.packed_tps || 0) / tpCount : 0;
        packed += Math.round(mine.length * ratio);
      });

      const percent =
        customers > 0 ? Math.round((packed / customers) * 100) : 0;

      return {
        cp_db_id: db.cp_db_id,
        db_name: db.db_name,
        customers,
        packed,
        percent,
      };
    });
  }, [data]);

export default usePackingDbs;
